import React, { useState, useEffect } from 'react';
import b2bService from '../../services/b2bService';
import { useAuth } from '../../context/AuthContext';
import B2bInboundPanel from './B2bInboundPanel';
import B2bApiKeysPanel from './B2bApiKeysPanel';
import B2bPaymentModal from './B2bPaymentModal';
import B2bTopupApprovalPanel from './B2bTopupApprovalPanel';
import './B2bCorporateBillingPage.css';
import './B2bPortalOrderHistoryPage.css';

const B2bCorporateBillingPage = () => {
  const { user } = useAuth();
  const centreId = user?.centreId || 1;   // invoices are raised for the signed-in centre
  const isAdmin = user?.roleName?.toUpperCase() === 'ADMINISTRATOR' || user?.roleId === 1;
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [parties, setParties] = useState([]);
  const [selectedPartyId, setSelectedPartyId] = useState('');
  const [activeTab, setActiveTab] = useState('UNBILLED'); // UNBILLED, ANOMALIES, INVOICES, AGING, INBOUND, APIKEYS
  const [unbilledOrders, setUnbilledOrders] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [agingReport, setAgingReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [billingFrom, setBillingFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [billingTo, setBillingTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Load eligible B2B parties
  useEffect(() => {
    b2bService.getEligibleParties().then((res) => {
      setParties(res);
      if (res.length > 0) {
        setSelectedPartyId(res[0].partyID);
      }
    }).catch(err => console.error('Error fetching parties:', err));
  }, []);

  // Fetch data based on active tab & selected party
  useEffect(() => {
    if (!selectedPartyId) return;
    loadTabData();
  }, [selectedPartyId, activeTab, billingFrom, billingTo]);

  const loadTabData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'UNBILLED') {
        const data = await b2bService.getUnbilledOrders(selectedPartyId, billingFrom, billingTo, centreId);
        setUnbilledOrders(data);
      } else if (activeTab === 'ANOMALIES') {
        const data = await b2bService.getBillingAnomalies(selectedPartyId, billingFrom, billingTo);
        setAnomalies(data);
      } else if (activeTab === 'INVOICES') {
        const data = await b2bService.getInvoices({ partyId: selectedPartyId });
        setInvoices(data);
      } else if (activeTab === 'AGING') {
        const data = await b2bService.getAgingReport({ partyId: selectedPartyId });
        setAgingReport(data);
      }
    } catch (err) {
      console.error('Error loading corporate billing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBatch = async () => {
    if (unbilledOrders.length === 0) {
      alert('There are no eligible unbilled orders in this date range.');
      return;
    }

    if (!window.confirm(`Generate official batch invoice for ${unbilledOrders.length} unbilled orders?`)) {
      return;
    }

    setGenerating(true);
    try {
      const res = await b2bService.generateBatchInvoice({
        partyId: Number(selectedPartyId),
        centreId,
        billingPeriodFrom: billingFrom,
        billingPeriodTo: billingTo,
        notes: `Automated Corporate Billing Run for period ${billingFrom} to ${billingTo}`
      });

      alert(`Invoice Generated Successfully!\nInvoice No: ${res.invoiceNumber}\nTotal Orders: ${res.totalOrders}\nNet Amount: ₹${res.netAmount}`);
      setActiveTab('INVOICES');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error generating batch invoice.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="b2b-billing-page">
      <div className="b2b-dashboard-header">
        <div>
          <h2 className="b2b-dashboard-title">B2B Corporate Invoicing &amp; Credit Control</h2>
          <div className="b2b-dashboard-subtitle">
            Enterprise batch invoicing, rate snapshot audits, pre-billing anomaly detection, and accounts receivable aging.
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <label style={{ fontSize: '13px', fontWeight: '700' }}>Client / Party:</label>
          <select
            className="b2b-filter-input"
            value={selectedPartyId}
            onChange={(e) => setSelectedPartyId(e.target.value)}
            style={{ fontWeight: '600' }}
          >
            {parties.map((p) => (
              <option key={p.partyID} value={p.partyID}>
                {p.partyName} ({p.partyCode}) &mdash; Limit: ₹{Number(p.creditLimit).toLocaleString()}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="b2b-billing-tabs">
        <button
          className={`b2b-tab-btn ${activeTab === 'UNBILLED' ? 'active' : ''}`}
          onClick={() => setActiveTab('UNBILLED')}
        >
          Unbilled Orders ({unbilledOrders.length})
        </button>
        <button
          className={`b2b-tab-btn ${activeTab === 'ANOMALIES' ? 'active' : ''}`}
          onClick={() => setActiveTab('ANOMALIES')}
        >
          AI Anomaly Warnings ({anomalies.length})
        </button>
        <button
          className={`b2b-tab-btn ${activeTab === 'INVOICES' ? 'active' : ''}`}
          onClick={() => setActiveTab('INVOICES')}
        >
          Corporate Invoices
        </button>
        <button
          className={`b2b-tab-btn ${activeTab === 'AGING' ? 'active' : ''}`}
          onClick={() => setActiveTab('AGING')}
        >
          AR Aging Ledger
        </button>
        <button
          className={`b2b-tab-btn ${activeTab === 'INBOUND' ? 'active' : ''}`}
          onClick={() => setActiveTab('INBOUND')}
        >
          Order Intake
        </button>
        {isAdmin && (
          <button
            className={`b2b-tab-btn ${activeTab === 'APIKEYS' ? 'active' : ''}`}
            onClick={() => setActiveTab('APIKEYS')}
          >
            API Keys
          </button>
        )}
        <button
          className={`b2b-tab-btn ${activeTab === 'TOPUPS' ? 'active' : ''}`}
          onClick={() => setActiveTab('TOPUPS')}
        >
          Top-Up Approvals
        </button>
      </div>

      {/* Date filter bar for Unbilled & Anomalies */}
      {(activeTab === 'UNBILLED' || activeTab === 'ANOMALIES') && (
        <div className="b2b-filter-bar">
          <div className="b2b-filter-group">
            <label className="b2b-filter-label">Period From</label>
            <input
              type="date"
              className="b2b-filter-input"
              value={billingFrom}
              onChange={(e) => setBillingFrom(e.target.value)}
            />
          </div>
          <div className="b2b-filter-group">
            <label className="b2b-filter-label">Period To</label>
            <input
              type="date"
              className="b2b-filter-input"
              value={billingTo}
              onChange={(e) => setBillingTo(e.target.value)}
            />
          </div>
          {activeTab === 'UNBILLED' && (
            <button
              className="b2b-refresh-btn"
              style={{ background: '#2563eb', color: '#fff', alignSelf: 'flex-end', marginLeft: 'auto' }}
              onClick={handleGenerateBatch}
              disabled={generating || unbilledOrders.length === 0}
            >
              {generating ? 'Processing Batch...' : `Generate Batch Invoice (₹${unbilledOrders.reduce((sum, o) => sum + Number(o.netAmount || 0), 0).toFixed(2)})`}
            </button>
          )}
        </div>
      )}

      {/* Content based on tab */}
      {activeTab === 'INBOUND' ? (
        <B2bInboundPanel />
      ) : activeTab === 'APIKEYS' ? (
        <B2bApiKeysPanel partyId={selectedPartyId} />
      ) : activeTab === 'TOPUPS' ? (
        <B2bTopupApprovalPanel />
      ) : loading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>Loading billing records...</div>
      ) : activeTab === 'UNBILLED' ? (
        <div className="b2b-table-card">
          {unbilledOrders.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              No unbilled orders found for this party in the selected billing period.
            </div>
          ) : (
            <table className="b2b-data-table">
              <thead>
                <tr>
                  <th>Order Date</th>
                  <th>Order Ref</th>
                  <th>Patient Name</th>
                  <th>Barcode</th>
                  <th>Investigation Item</th>
                  <th>Contract Rate (₹)</th>
                  <th>Discount (₹)</th>
                  <th>Net Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {unbilledOrders.map((o, idx) => (
                  <tr key={idx}>
                    <td>{new Date(o.orderDate).toLocaleDateString('en-IN')}</td>
                    <td><strong>{o.ledgerTransactionNo}</strong></td>
                    <td>{o.patientName} ({o.uhid})</td>
                    <td><code>{o.barcodeNo}</code></td>
                    <td>{o.itemName}</td>
                    <td>₹{Number(o.rate).toFixed(2)}</td>
                    <td>₹{Number(o.discountAmt).toFixed(2)}</td>
                    <td style={{ fontWeight: '700' }}>₹{Number(o.netAmount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : activeTab === 'ANOMALIES' ? (
        <div>
          {anomalies.length === 0 ? (
            <div className="b2b-table-card" style={{ padding: '40px', textAlign: 'center', color: '#10b981' }}>
              &check; All unbilled transactions passed automated AI tariff and duplicate detection checks!
            </div>
          ) : (
            anomalies.map((a, idx) => (
              <div key={idx} className={`b2b-anomaly-card ${a.severity === 'HIGH' ? 'high' : ''}`}>
                <div className="b2b-anomaly-header">
                  <span>{a.type} &bull; Order Ref: {a.ledgerTransactionNo}</span>
                  <span style={{ fontSize: '11px', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '4px', background: a.severity === 'HIGH' ? '#fee2e2' : '#fef3c7', color: a.severity === 'HIGH' ? '#b91c1c' : '#b45309' }}>
                    {a.severity} Severity (Confidence: {Math.round(a.confidenceScore * 100)}%)
                  </span>
                </div>
                <div className="b2b-anomaly-evidence">
                  {a.evidence}
                </div>
              </div>
            ))
          )}
        </div>
      ) : activeTab === 'INVOICES' ? (
        <div className="b2b-table-card">
          <table className="b2b-data-table">
            <thead>
              <tr>
                <th>Invoice No</th>
                <th>Billing Period</th>
                <th>Invoice Date</th>
                <th>Due Date</th>
                <th>Total Orders</th>
                <th>Net Amount (₹)</th>
                <th>Paid (₹)</th>
                <th>Balance (₹)</th>
                <th>Status</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.invoiceID}>
                  <td><strong>{inv.invoiceNumber}</strong></td>
                  <td>{new Date(inv.billingPeriodFrom).toLocaleDateString('en-IN')} &ndash; {new Date(inv.billingPeriodTo).toLocaleDateString('en-IN')}</td>
                  <td>{new Date(inv.invoiceDate).toLocaleDateString('en-IN')}</td>
                  <td>{new Date(inv.dueDate).toLocaleDateString('en-IN')}</td>
                  <td>{inv.totalOrders}</td>
                  <td><strong>₹{Number(inv.netAmount).toFixed(2)}</strong></td>
                  <td style={{ color: '#10b981' }}>₹{Number(inv.paidAmount).toFixed(2)}</td>
                  <td style={{ color: Number(inv.balanceAmount) > 0 ? '#ef4444' : '#64748b' }}>₹{Number(inv.balanceAmount).toFixed(2)}</td>
                  <td>
                    <span className={`b2b-badge ${inv.status === 'PAID' ? 'completed' : 'pending'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td>
                    {Number(inv.balanceAmount) > 0 && (
                      <button type="button" className="b2b-refresh-btn" onClick={() => setPayingInvoice(inv)}>Record payment</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="b2b-table-card">
          <table className="b2b-data-table">
            <thead>
              <tr>
                <th>Client / Party</th>
                <th>Credit Limit (₹)</th>
                <th>Current Outstanding (₹)</th>
                <th>0 &ndash; 30 Days (₹)</th>
                <th>31 &ndash; 60 Days (₹)</th>
                <th>61 &ndash; 90 Days (₹)</th>
                <th>90+ Days (₹)</th>
              </tr>
            </thead>
            <tbody>
              {agingReport.map((ag) => (
                <tr key={ag.partyID}>
                  <td><strong>{ag.partyName}</strong> ({ag.partyCode})</td>
                  <td>₹{Number(ag.creditLimit).toLocaleString()}</td>
                  <td style={{ fontWeight: '800', color: '#ef4444' }}>₹{Number(ag.totalOutstanding).toFixed(2)}</td>
                  <td>₹{Number(ag.current0_30).toFixed(2)}</td>
                  <td>₹{Number(ag.bracket31_60).toFixed(2)}</td>
                  <td>₹{Number(ag.bracket61_90).toFixed(2)}</td>
                  <td style={{ fontWeight: '700', color: Number(ag.bracket90Plus) > 0 ? '#b91c1c' : '#64748b' }}>
                    ₹{Number(ag.bracket90Plus).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {payingInvoice && (
        <B2bPaymentModal
          invoice={payingInvoice}
          onClose={() => setPayingInvoice(null)}
          onDone={() => { setPayingInvoice(null); loadTabData(); }}
        />
      )}
    </div>
  );
};

export default B2bCorporateBillingPage;
